import type { LogEntry } from '@/lib/types'
import type { ReactNode } from 'react'

type ReadableArtifactProps = {
  artifactId: string
  data: any
}

function labelize(value: string) {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function formatValue(value: any): string {
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') return value.toLocaleString()
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function KeyValueGrid({ data }: { data: Record<string, any> }) {
  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
      {Object.entries(data).map(([key, value]) => (
        <div key={key} className="rounded-md border border-slate-800 bg-slate-950/50 p-3">
          <div className="text-xs text-slate-500">{labelize(key)}</div>
          <div className="mt-1 text-sm text-slate-200">{formatValue(value)}</div>
        </div>
      ))}
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h4 className="text-sm font-semibold text-slate-200">{title}</h4>
      {children}
    </section>
  )
}

function ListSection({ items }: { items: any[] }) {
  if (!items.length) return <div className="text-sm text-slate-500">None</div>

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="rounded-md border border-slate-800 bg-slate-950/50 p-3 text-sm">
          {typeof item === 'object' && item !== null ? <KeyValueGrid data={item} /> : formatValue(item)}
        </div>
      ))}
    </div>
  )
}

function FiltersView({ data }: { data: any }) {
  return (
    <div className="space-y-4">
      {data.reasoning && (
        <Section title="Search Reasoning">
          <KeyValueGrid data={data.reasoning} />
        </Section>
      )}
      {data.signals && (
        <Section title="Signals Used">
          <KeyValueGrid data={data.signals} />
        </Section>
      )}
      {data.locked && (
        <Section title="Locked Filters">
          <KeyValueGrid data={data.locked} />
        </Section>
      )}
      {data.resolution && (
        <Section title="Resolved Filter IDs">
          <KeyValueGrid data={data.resolution} />
        </Section>
      )}
      {data.payload && (
        <Section title="Sales Navigator Payload">
          <KeyValueGrid data={data.payload} />
        </Section>
      )}
    </div>
  )
}

function InterpretationView({ data }: { data: any }) {
  return (
    <div className="space-y-4">
      {data.ideal_profile && (
        <Section title="Ideal Profile">
          <KeyValueGrid data={data.ideal_profile} />
        </Section>
      )}
      {data.signal_weights && (
        <Section title="Signal Weights">
          <KeyValueGrid data={data.signal_weights} />
        </Section>
      )}
      <Section title="Excluded">
        <ListSection items={data.excluded || []} />
      </Section>
    </div>
  )
}

function PipelineLogView({ data }: { data: LogEntry[] }) {
  return (
    <div className="max-h-[520px] overflow-auto rounded-md border border-slate-800">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-slate-900">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-slate-400">Time</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-slate-400">Event</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-slate-400">Details</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {data.map((entry, index) => (
            <tr key={index} className="align-top hover:bg-slate-900">
              <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-slate-400">
                {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '-'}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-xs font-medium uppercase text-slate-300">
                {entry.event}
              </td>
              <td className="px-3 py-2 text-slate-300">
                {entry.message || entry.label || entry.status || entry.phase || formatValue(entry.details)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function ReadableArtifact({ artifactId, data }: ReadableArtifactProps) {
  if (artifactId === 'filters') return <FiltersView data={data} />
  if (artifactId === 'interpretation') return <InterpretationView data={data} />
  if (artifactId === 'pipeline_log' && Array.isArray(data)) return <PipelineLogView data={data} />

  if (Array.isArray(data)) return <ListSection items={data} />
  if (typeof data === 'object' && data !== null) return <KeyValueGrid data={data} />

  return <div className="text-sm text-slate-300">{formatValue(data)}</div>
}
